/**
 * reCAPTCHA Enterprise v3 utilities
 * Handles token generation and server-side verification
 */

import { buildAppsationsFunctionUrl } from '@/lib/appsationsSdkEndpoints';
import { RECAPTCHA_SITE_KEY, ensureRecaptchaEnterpriseScript } from '@/lib/recaptchaConfig';

// Type declarations for reCAPTCHA Enterprise are in Index.tsx
// to avoid conflicts with declare global

const VERIFY_ENDPOINT = buildAppsationsFunctionUrl('verifyRecaptchaToken_endpoint');

/**
 * Execute reCAPTCHA Enterprise and get a token
 */
export async function getRecaptchaToken(action: string = 'SUBMIT'): Promise<string> {
  return new Promise((resolve, reject) => {
    ensureRecaptchaEnterpriseScript();

    if (!window.grecaptcha?.enterprise) {
      console.warn('[recaptcha] grecaptcha.enterprise not available');
      resolve('');
      return;
    }

    window.grecaptcha.enterprise.ready(() => {
      if (!RECAPTCHA_SITE_KEY) {
        console.warn('[recaptcha] site key not configured');
        resolve('');
        return;
      }

      window.grecaptcha
        .enterprise!.execute(RECAPTCHA_SITE_KEY, { action })
        .then((token) => resolve(token))
        .catch((err) => {
          console.error('[recaptcha] token generation failed:', err);
          resolve('');
        });
    });
  });
}

/**
 * Verify token server-side and return validation result
 */
export async function verifyRecaptchaTokenServer(
  token: string,
  action: string = 'SUBMIT',
  minScore: number = 0.5
): Promise<{ isValid: boolean; score?: number }> {
  if (!token) {
    return { isValid: false };
  }

  try {
    const response = await fetch(VERIFY_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, action, minScore }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      console.error('[recaptcha] verification failed:', error);
      return { isValid: false };
    }

    return response.json();
  } catch (err) {
    console.error('[recaptcha] verification request failed:', err);
    return { isValid: false };
  }
}

/**
 * Complete flow: get token and verify it
 * Returns { isValid, score } or throws on critical errors
 */
export async function executeAndVerifyRecaptcha(
  action: string = 'SUBMIT',
  verifyServer: boolean = true,
  minScore: number = 0.5
): Promise<{ token: string; isValid: boolean; score?: number }> {
  const token = await getRecaptchaToken(action);

  if (!verifyServer) {
    return { token, isValid: Boolean(token) };
  }

  const result = await verifyRecaptchaTokenServer(token, action, minScore);
  return { token, ...result };
}
